import React, { useMemo } from 'react';
import {
  LinkedinVerificationStatus,
  Participant,
  useDisconnectLinkedinAccount,
  useLinkedinAccount,
  useLinkedinLogin,
  useMe,
} from '@app/api';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { Button, ChipColor, LabeledIconChip } from '@app/core';
import classnames from 'classnames';
import { Input } from '@app/form';

const useStyles = createUseStyles((theme: Theme) => ({
  linkedinContainer: {
    marginTop: theme.marginBase * 6,
  },
  linkedinButton: {
    width: '100%',
    marginTop: theme.marginBase,
  },
  linkedinIcon: {
    marginRight: theme.marginBase,
    fontSize: theme.iconSize.large,
    color: theme.colors.white,
  },
  iconTitle: {
    fontSize: theme.iconSize.large,
    color: theme.colors.blueAccessible,
  },
  infoLinkedinContainer: {
    marginTop: theme.marginBase * 2,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    backgroundColor: theme.colors.grayBackground,
    whiteSpace: 'pre-line',
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  titleContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  linkedinHeader: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.marginBase,
  },
  linkedinLabel: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
  },
  linkedinDisconnect: {
    minHeight: 0,
    minWidth: 0,
    '&:hover:not(:disabled)': {
      backgroundColor: 'transparent',
    },
    '&:active:not(:disabled)': {
      backgroundColor: 'transparent',
    },
    '&:focus:not(:disabled)': {
      backgroundColor: 'transparent',
    },
  },
  linkedinDisconnectContent: {
    padding: 0,
  },
  chip: {
    whiteSpace: 'nowrap',
  },
}));

interface Props {
  participant: Participant;
  hide?: boolean;
}

export const LinkedinConnection = ({ hide, participant }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: me } = useMe();

  const { data: linkedinAccount } = useLinkedinAccount(me?.id);
  const { mutate: disconnectLinkedin } = useDisconnectLinkedinAccount(
    me?.id,
    participant?.id,
  );
  const { data: linkedinAuthorizationURL } = useLinkedinLogin();

  const linkedinIcon = useMemo(() => {
    switch (participant?.linkedinVerificationStatus) {
      case LinkedinVerificationStatus.VERIFIED:
        return { icon: 'ri-checkbox-circle-fill', color: 'green' };
      case LinkedinVerificationStatus.VERIFICATION_PENDING:
        return { icon: 'ri-loader-4-line', color: 'yellow' };
      case LinkedinVerificationStatus.NOT_VERIFIED:
        return { icon: 'ri-close-circle-fill', color: 'red' };
      default:
        return { icon: 'ri-question-mark', color: 'red' };
    }
  }, [participant?.linkedinVerificationStatus]);

  if (!linkedinAccount && hide) {
    return null;
  }

  return (
    <div className={classes.linkedinContainer}>
      <div className={classes.linkedinHeader}>
        <div className={classes.titleContainer}>
          <i
            className={classnames('ri-linkedin-box-fill', classes.iconTitle)}
          />
          <p className={classes.linkedinLabel}>
            {t('participantSpace.professionalProfile.linkedin.label')}
          </p>
        </div>
        <LabeledIconChip
          chipLabel={t(
            `participantSpace.professionalProfile.linkedin.verificationStatus.${participant.linkedinVerificationStatus}`,
          )}
          chipIcon={linkedinIcon.icon}
          className={classes.chip}
          labelColor={linkedinIcon.color as ChipColor}
          color="green"
        />
      </div>

      {!linkedinAccount && linkedinAuthorizationURL?.redirectUrl ? (
        <>
          <Button
            className={classes.linkedinButton}
            href={linkedinAuthorizationURL.redirectUrl}
          >
            <i
              className={classnames(
                'ri-linkedin-box-fill',
                classes.linkedinIcon,
              )}
            />
            {t('participantSpace.professionalProfile.linkedin.button')}
          </Button>
          <div className={classes.infoLinkedinContainer}>
            {t('participantSpace.professionalProfile.linkedin.info')}
          </div>
        </>
      ) : (
        <Button
          contentClassName={classes.linkedinDisconnectContent}
          className={classes.linkedinDisconnect}
          variant="transparent"
          text={t('participantSpace.professionalProfile.linkedin.disconnect')}
          onClick={() => disconnectLinkedin({})}
        />
      )}
    </div>
  );
};
